import { useEffect, useMemo } from 'react'
import { usePermissions } from '../lib/usePermissions.jsx'
import { useNavigate, useLocation } from 'react-router-dom'

const PermissionRouteGuard = ({ children, requiredTab, requiredPermission }) => {
  const { loading, permissions, isOwner } = usePermissions()
  const navigate = useNavigate()
  const location = useLocation()

  // Memoize route type checks - only recompute when pathname changes
  const { isBrandingRoute, isBillingRoute, isOwnerOnlyRoute } = useMemo(() => {
    const brandingRoute = location.pathname === '/settings/branding' || location.pathname === '/company-branding'
    const billingRoute = location.pathname === '/settings/billing' || location.pathname.startsWith('/subscription/')
    return {
      isBrandingRoute: brandingRoute,
      isBillingRoute: billingRoute,
      isOwnerOnlyRoute: brandingRoute || billingRoute
    }
  }, [location.pathname])

  // Memoize permission checks - only recompute when dependencies change
  const { hasTabAccess, hasActionAccess, hasAccess } = useMemo(() => {
    // Settings tab is always accessible (cannot be restricted)
    if (requiredTab === 'settings') {
      return { hasTabAccess: true, hasActionAccess: true, hasAccess: true }
    }

    const tabAccess = requiredTab ? (permissions[requiredTab] === true || isOwner) : true
    const actionAccess = requiredPermission ? (permissions[requiredPermission] === true || isOwner) : true

    return {
      hasTabAccess: tabAccess,
      hasActionAccess: actionAccess,
      hasAccess: tabAccess && actionAccess
    }
  }, [requiredTab, requiredPermission, permissions, isOwner])

  // Handle redirect when user doesn't have permission
  useEffect(() => {
    if (loading) return

    // Block employees from accessing owner-only routes
    if (isOwnerOnlyRoute && !isOwner) {
      navigate('/settings', { replace: true })
      return
    }

    // Settings is always accessible - no redirect needed
    if (requiredTab === 'settings') {
      return
    }

    // Redirect if no access
    if (!hasAccess) {
      navigate('/dashboard', { replace: true })
    }
  }, [loading, isOwnerOnlyRoute, isOwner, requiredTab, hasAccess, navigate])

  // Show loading state while permissions are being loaded
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-secondary">Loading permissions...</p>
        </div>
      </div>
    )
  }

  // Block employees from accessing owner-only routes (uses memoized check)
  if (isOwnerOnlyRoute && !isOwner) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-mint-50 to-primary-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-20 h-20 rounded-2xl bg-red-500 flex items-center justify-center shadow-glow mb-6 mx-auto">
            <i className="fas fa-exclamation-triangle text-white text-2xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-primary mb-4">Access Denied</h1>
          <p className="text-secondary mb-6">
            This section is only available to company owners.
          </p>
          <button
            onClick={() => navigate('/settings')}
            className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Go to Settings
          </button>
        </div>
      </div>
    )
  }

  // Settings is always accessible to employees (handled in memoized hasAccess)
  // If no permission, show access denied (redirect will happen via useEffect)
  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-mint-50 to-primary-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-20 h-20 rounded-2xl bg-red-500 flex items-center justify-center shadow-glow mb-6 mx-auto">
            <i className="fas fa-exclamation-triangle text-white text-2xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-primary mb-4">Access Denied</h1>
          <p className="text-secondary mb-6">
            You don&apos;t have permission to access {requiredTab ? (<strong>{requiredTab}</strong>) : 'this section'}{requiredPermission ? (<> (requires <strong>{requiredPermission}</strong>)</>) : null}.
          </p>
          <p className="text-sm text-muted mb-6">
            Please contact your administrator if you believe this is an error.
          </p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    )
  }

  // User has permission, render the protected content
  return children
}

export default PermissionRouteGuard 